import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, type ApiClient } from './api-client';

/**
 * The two data hooks every screen uses.
 *
 * They exist so screens do not each reinvent loading state, cancellation and error mapping —
 * and so a stale response from an abandoned request can never overwrite a newer one, which is
 * the bug that makes search boxes flicker between results.
 */

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | Error | null;
  isLoading: boolean;
  /** True on a refetch while previous data is still on screen. */
  isRefreshing: boolean;
  reload: () => void;
}

/**
 * Loads data when its dependencies change.
 *
 * `deps` is the dependency list, exactly as a `useEffect` would take it. The fetcher receives
 * an AbortSignal — pass it to the API client so an abandoned request is actually cancelled
 * rather than merely ignored.
 */
export function useAsync<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);

  const hasData = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    if (hasData.current) setIsRefreshing(true);
    else setIsLoading(true);

    fetcherRef
      .current(controller.signal)
      .then((result) => {
        // A response from a superseded request must not land: the effect that started it has
        // already been cleaned up, and its data is for a query the user has moved on from.
        if (cancelled) return;

        setData(result);
        setError(null);
        hasData.current = true;
      })
      .catch((caught: unknown) => {
        if (cancelled) return;

        // An abort is the expected outcome of navigating away, not an error to show.
        if (caught instanceof DOMException && caught.name === 'AbortError') return;

        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => {
        if (cancelled) return;

        setIsLoading(false);
        setIsRefreshing(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, isLoading, isRefreshing, reload };
}

export interface MutationState<TInput, TResult> {
  run: (input: TInput) => Promise<TResult | null>;
  isRunning: boolean;
  error: ApiError | Error | null;
  reset: () => void;
}

/**
 * Runs a write and tracks its state.
 *
 * Returns null on failure rather than throwing, so a caller can write
 * `if (await run(x))` without a try/catch around every button handler. The error is exposed
 * on the returned state for display.
 *
 * Handlers that need the error immediately after awaiting `run` must use `onError` rather than
 * reading `error`: the handler closed over the state from the render that created it, so
 * `error` there is still whatever it was before the call.
 */
export function useMutation<TInput, TResult>(
  action: (input: TInput) => Promise<TResult>,
  options: {
    onSuccess?: (result: TResult) => void;
    onError?: (error: ApiError | Error) => void;
  } = {},
): MutationState<TInput, TResult> {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const actionRef = useRef(action);
  actionRef.current = action;

  const onSuccessRef = useRef(options.onSuccess);
  onSuccessRef.current = options.onSuccess;

  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;

  const run = useCallback(async (input: TInput): Promise<TResult | null> => {
    setIsRunning(true);
    setError(null);

    try {
      const result = await actionRef.current(input);
      onSuccessRef.current?.(result);
      return result;
    } catch (caught) {
      const failure = caught instanceof Error ? caught : new Error(String(caught));

      setError(failure);
      onErrorRef.current?.(failure);

      return null;
    } finally {
      setIsRunning(false);
    }
  }, []);

  const reset = useCallback(() => setError(null), []);

  return { run, isRunning, error, reset };
}

/** Delays a fast-changing value — search boxes, mostly. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Generates a stable idempotency key for the lifetime of a form. */
export function useIdempotencyKey(): string {
  const [key] = useState(() => crypto.randomUUID());
  return key;
}

/**
 * An object URL for authenticated binary content, for use as an `<img>` or `<iframe>` source.
 *
 * The element cannot fetch it itself: a `src` attribute produces an anonymous request, which the
 * API refuses. So the bytes are fetched with the session's token and handed over as a blob.
 *
 * The URL is revoked when it changes or the component unmounts. Without that, every preview
 * opened in a session stays in memory until the tab is closed — which for a screen full of phone
 * photos is tens of megabytes.
 */
export function useObjectUrl(
  api: ApiClient,
  path: string | null,
): { url: string | null; isLoading: boolean; error: Error | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      setError(null);
      return;
    }

    let cancelled = false;
    let created: string | null = null;

    setIsLoading(true);
    setError(null);

    api
      .fetchBlob(path)
      .then((blob: Blob) => {
        if (cancelled) return;

        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;

        setError(caught instanceof Error ? caught : new Error('Could not load the file.'));
        setUrl(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [api, path]);

  return { url, isLoading, error };
}
